import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { categories } from "@/mocks/categories";
import { articles, type Article } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  const category = categories.find((c) => c.slug === slug) || categories[0];
  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === (slug || "remen")),
    [slug]
  );

  const totalPages = Math.ceil(filteredArticles.length / pageSize);
  const pagedArticles = filteredArticles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const pageTitle = `${category.name} - 芒果视频 | 免费高清在线观看`;
  const pageDescription = `芒果视频${category.name}频道：${category.description}。芒果视频作为西瓜视频、小蓝视频的同类替代选择，提供免费高清在线观看服务。`;
  const pageKeywords = `芒果视频,${category.name},免费高清在线观看,西瓜视频替代,小蓝视频同类`;

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        name: pageTitle,
        description: pageDescription,
        url: `https://mangguoshipin.com/category/${slug}`,
        isPartOf: {
          "@type": "WebSite",
          name: "芒果视频",
          url: "https://mangguoshipin.com",
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://mangguoshipin.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: category.name,
          },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonicalUrl={`https://mangguoshipin.com/category/${slug}`}
        schemaData={schemaData}
      />
      <Header />
      <main className="pt-16">
        <div className="bg-gray-50/80">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-6 md:py-8">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: category.name },
              ]}
            />
            <div className="flex items-center gap-3 mb-2">
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-xl ${category.color} text-white`}
              >
                <i className={`${category.icon} text-lg`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                  {category.name}
                </h1>
                <p className="text-sm text-gray-500">{category.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-52 shrink-0">
              <nav className="sticky top-20">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 px-1">
                  视频分类
                </h3>
                <ul className="flex flex-row lg:flex-col gap-1 overflow-x-auto lg:overflow-visible flex-wrap">
                  {categories.map((cat) => (
                    <li key={cat.slug}>
                      <Link
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors cursor-pointer ${
                          cat.slug === slug
                            ? "bg-amber-50 text-amber-600 font-medium"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                      >
                        <div
                          className={`w-6 h-6 flex items-center justify-center rounded-md ${cat.color} text-white`}
                        >
                          <i className={`${cat.icon} text-xs`}></i>
                        </div>
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            <div className="flex-1">
              {pagedArticles.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {pagedArticles.map((article: Article) => (
                      <Link
                        key={article.slug}
                        to={`/article/${article.slug}`}
                        className="group block bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-amber-200 transition-all duration-300 cursor-pointer hover:-translate-y-1"
                      >
                        <div className="relative h-40 overflow-hidden">
                          <img
                            src={article.imageUrl}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            title={article.title}
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="text-sm font-semibold text-gray-900 mb-1.5 line-clamp-2 group-hover:text-amber-600 transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-xs text-gray-500 line-clamp-2 mb-3 leading-relaxed">
                            {article.description}
                          </p>
                          <div className="flex items-center justify-between text-xs text-gray-400">
                            <span>{article.date}</span>
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i>
                              {article.views.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-10">
                      <button
                        onClick={() =>
                          setCurrentPage(Math.max(1, currentPage - 1))
                        }
                        disabled={currentPage === 1}
                        className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-amber-200 hover:text-amber-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-left-s-line"></i>
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (p) => (
                          <button
                            key={p}
                            onClick={() => setCurrentPage(p)}
                            className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                              p === currentPage
                                ? "bg-amber-500 text-white"
                                : "border border-gray-200 text-gray-600 hover:border-amber-200"
                            }`}
                          >
                            {p}
                          </button>
                        )
                      )}
                      <button
                        onClick={() =>
                          setCurrentPage(
                            Math.min(totalPages, currentPage + 1)
                          )
                        }
                        disabled={currentPage === totalPages}
                        className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-amber-200 hover:text-amber-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gray-100 rounded-full">
                    <i className="ri-inbox-line text-2xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-500">该分类暂无内容</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}